import Image from "@Components/Image";
import styles from "./Ticker.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import { pageAsset } from "@Utils/getStaticAssetPath";

// Images come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("ListWithUsV2");

const studentRoost = asset("logos/student-roost.png");
const uniteStudents = asset("logos/unite-students.png");
const iqStudent = asset("logos/iq-student.png");
const homesForStudents = asset("logos/homes-for-students.png");
const collegiate = asset("logos/collegiate.png");
const greystar = asset("logos/greystar.png");
const dwellStudent = asset("logos/dwell-student.png");
const vitaStudent = asset("logos/vita-student.png");
const helloStudent = asset("logos/hello-student.png");
const fresh = asset("logos/fresh.png");
const crmStudents = asset("logos/crm-students.png");
const varsity = asset("logos/varsity.png");
const americanCampus = asset("logos/american-campus.png");
const habyt = asset("logos/habyt.png");
const assetLiving = asset("logos/asset-living.png");
const juneHomes = asset("logos/june-homes.png");
const scape = asset("logos/scape.png");
const unilodge = asset("logos/unilodge.png");
const campusLivingVillages = asset("logos/campus-living-villages.png");
const axoStudent = asset("logos/axo-student.png");

/**
 * Partner logo wall — Figma node 2483:9993, which drew TWO ROWS OF FOUR. It holds
 * twenty now, grouped by region, so the node's fixed arrangement no longer describes
 * it; `Audience`'s `.marksGrid` wraps them and was re-justified to suit (see the note
 * there — `space-between` only works for a single row).
 *
 * Rendered by Hero — in this version of the design the wall sits inside the hero
 * section, not in a band of its own.
 */
export type OperatorLogo = {
  src: string;
  alt: string;
  width: number;
  height: number;
};

/**
 * The operator marks, GROUPED BY REGION — UK & Europe, then the US, then Asia-Pacific.
 * They were ordered by name size when there were eight; twenty is enough that the
 * grouping carries more meaning than the ranking, and it is how the list was handed
 * over.
 *
 * ⚠️  `width`/`height` ARE COMPUTED FROM THE ARTWORK, not read off a Figma node — the
 * node only ever covered eight of these. Each mark's file is measured, then sized to
 * `min(36px tall, 112px wide)`: a cap on BOTH axes, because a wordmark like Habyt is
 * 5.6:1 and would run 200px at a shared height, while dwell is nearly square. One
 * blanket height makes a wall of wildly different optical weights; capping both keeps
 * every mark inside the same box.
 *
 * ⚠️  TWO OF THESE ARE REVERSED MARKS — dwell and UniLodge are white knocked out of a
 * red plate. They are NOT silhouetted like the rest, which would fill them in as solid
 * blocks; the plate is recoloured to black and the knockout kept. UniLodge additionally sets "Uni" in white and "Lodge" in BLACK on
 * that red, so a naive recolour loses half the word — both are knocked out white here,
 * because a monochrome reduction cannot carry two inks.
 *
 * Exported because this wall is not the only place they are drawn any more: "Who we work
 * with" (`Audience`) ends on a strip of them, the way Partner With Us's equivalent section
 * does. This stays the single source of truth — the two-row wall below is one arrangement
 * of the same list, not a second copy of it.
 *
 * ⚠️  THE ORDER IS MEANT. It runs biggest and best-known operator downward rather than
 * following the node's layout, because the strip's job is to be recognised: whoever is
 * reading has to hit a name they know in the first two marks or the wall is just texture.
 * The old row order opened on Fresh and buried Unite at the end of row one.
 */
export const OPERATOR_LOGOS: OperatorLogo[] = [
  // ── UK & Europe ───────────────────────────────────────────────────────────
  { src: studentRoost, alt: "Student Roost", width: 67, height: 23 },
  { src: uniteStudents, alt: "Unite Students", width: 69, height: 29 },
  { src: iqStudent, alt: "iQ Student Accommodation", width: 82, height: 27 },
  { src: homesForStudents, alt: "Homes for Students", width: 70, height: 30 },
  { src: collegiate, alt: "Collegiate", width: 92, height: 27 },
  { src: greystar, alt: "Greystar", width: 92, height: 17 },
  { src: dwellStudent, alt: "dwell Student Living", width: 38, height: 26 },
  { src: vitaStudent, alt: "Vita Student", width: 74, height: 24 },
  { src: helloStudent, alt: "Hello Student", width: 76, height: 22 },
  { src: fresh, alt: "Fresh", width: 65, height: 30 },
  { src: crmStudents, alt: "CRM Students", width: 64, height: 23 },
  { src: varsity, alt: "Varsity", width: 60, height: 29 },
  { src: axoStudent, alt: "AXO Student", width: 58, height: 19 },

  // ── United States ─────────────────────────────────────────────────────────
  { src: americanCampus, alt: "American Campus Communities", width: 84, height: 21 },
  { src: habyt, alt: "Habyt", width: 92, height: 17 },
  { src: assetLiving, alt: "Asset Living", width: 92, height: 18 },
  { src: juneHomes, alt: "June Homes", width: 67, height: 30 },

  // ── Asia-Pacific ──────────────────────────────────────────────────────────
  { src: scape, alt: "Scape", width: 62, height: 26 },
  { src: unilodge, alt: "UniLodge", width: 50, height: 20 },
  { src: campusLivingVillages, alt: "Campus Living Villages", width: 89, height: 22 },
];

/** The wall's two rows of four, taken off the one ordered list above. */
const ROWS = [OPERATOR_LOGOS.slice(0, 4), OPERATOR_LOGOS.slice(4)];

const Ticker = () => (
  <div className={styles.wall}>
    {ROWS.map((row, rowIndex) => (
      // eslint-disable-next-line react/no-array-index-key
      <div className={styles.row} key={rowIndex}>
        {row.map((logo) => (
          <div className={styles.cell} key={logo.alt}>
            {/* isNotLazy: the shared Image otherwise fades and scales each logo
                in from opacity 0 / scale(0.9). A partner wall should read as one
                steady row, not eight things popping in. */}
            <Image
              src={logo.src}
              alt={logo.alt}
              className={styles.logo}
              width={logo.width}
              height={logo.height}
              style={{ width: logo.width, height: logo.height }}
              isNotLazy
            />
          </div>
        ))}
      </div>
    ))}
  </div>
);

export default wrapperHOC(Ticker, {
  componentName: "Ticker-ListWithUs",
  showForChina: true,
});
